import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Loader2, CalendarClock, AlertCircle, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Textarea } from '../ui/textarea';
import { NextAvailableSlots } from '../service/NextAvailableSlots';
import { getNextAvailableSlots } from '../../utils/nextAvailableSlots';
import { api } from '../../App';

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
];
const DAY_MAP = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Reschedule picker that ONLY surfaces time-slots the provider is actually
 * available for, driven by the same /providers/{id}/calendar endpoint the
 * main booking flow uses. Removes the risk of a client choosing an arbitrary
 * date/time that conflicts with another booking or falls outside business hours.
 */
export function RescheduleDialog({ open, onOpenChange, booking, onRescheduled }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [reason, setReason] = useState('');
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const serviceDuration = Number(booking?.duration) || 60;
  const providerId = booking?.provider_id;

  const loadCalendar = useCallback(async () => {
    if (!providerId) return null;
    try {
      const res = await api.get(`/providers/${providerId}/calendar`);
      setAvailability(res.data);
      setFetchError(null);
      return res.data;
    } catch {
      setFetchError("We couldn't load the provider's calendar, please try again.");
      return null;
    }
  }, [providerId]);

  useEffect(() => {
    if (!open || !providerId) return;
    let cancelled = false;
    setLoading(true);
    setSelectedDate(null);
    setSelectedTime('');
    setReason('');
    loadCalendar().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, providerId, loadCalendar]);

  const computeSlotsForDate = useCallback((date) => {
    if (!date || !availability || !booking) return [];
    const dayName = DAY_MAP[date.getDay()];
    const schedule = availability.weekly_schedule?.[dayName];
    if (!schedule || !schedule.enabled) return [];
    const startMinutes = parseInt(schedule.start.split(':')[0]) * 60 + parseInt(schedule.start.split(':')[1]);
    const endMinutes = parseInt(schedule.end.split(':')[0]) * 60 + parseInt(schedule.end.split(':')[1]);
    const dateStr = format(date, 'yyyy-MM-dd');
    const blockedRanges = (availability.blocked_dates || [])
      .filter((b) => b.date === dateStr && !b.all_day && b.start_time && b.end_time)
      .map((b) => ({
        start: parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]),
        end: parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]),
      }));
    const bookedRanges = (availability.booked_slots || [])
      .filter((b) => b.date === dateStr)
      // Exclude the booking being rescheduled, its slot is about to free up.
      .filter((b) => !(b.date === booking?.date && b.time === booking?.time))
      .map((b) => {
        const [bh, bm] = b.time.split(':').map(Number);
        const startMin = bh * 60 + bm;
        return { start: startMin, end: startMin + (Number(b.duration) || 60) };
      });
    const minNotice = availability.min_notice_hours || 0;
    const now = new Date();
    const out = [];
    for (const slot of TIME_SLOTS) {
      const [h, m] = slot.split(':').map(Number);
      const mins = h * 60 + m;
      if (mins < startMinutes || mins >= endMinutes) continue;
      const slotEnd = mins + serviceDuration;
      if (slotEnd > endMinutes) continue;
      let inBlocked = false;
      for (const r of blockedRanges) {
        if (mins < r.end && slotEnd > r.start) { inBlocked = true; break; }
      }
      if (inBlocked) continue;
      if (minNotice > 0) {
        const slotDate = new Date(date);
        slotDate.setHours(h, m, 0, 0);
        if ((slotDate - now) / (1000 * 60 * 60) < minNotice) continue;
      }
      let booked = false;
      for (const r of bookedRanges) {
        if (mins < r.end && slotEnd > r.start) { booked = true; break; }
      }
      out.push({ time: slot, status: booked ? 'booked' : 'available' });
    }
    return out;
  }, [availability, booking, serviceDuration]);

  const isDateDisabled = useCallback((date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (!availability) return true;
    const minNotice = availability.min_notice_hours || 0;
    if (minNotice > 0) {
      const now = new Date();
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59);
      if ((endOfDay - now) / (1000 * 60 * 60) < minNotice) return true;
    }
    const dayName = DAY_MAP[date.getDay()];
    const schedule = availability.weekly_schedule?.[dayName];
    if (schedule && !schedule.enabled) return true;
    const dateStr = format(date, 'yyyy-MM-dd');
    if (availability.blocked_dates?.find((b) => b.date === dateStr && b.all_day)) return true;
    const slots = computeSlotsForDate(date);
    return slots.length > 0 && slots.every((s) => s.status === 'booked');
  }, [availability, computeSlotsForDate]);

  const slots = useMemo(() => (selectedDate ? computeSlotsForDate(selectedDate) : []), [selectedDate, computeSlotsForDate]);
  const hasSlots = slots.some((s) => s.status === 'available');

  const sameAsExisting = selectedDate
    && format(selectedDate, 'yyyy-MM-dd') === booking?.date
    && selectedTime === booking?.time;

  // Dead-end states: selected date has no openings, OR client picked the
  // exact same time as their current booking. In either case, surface 2-3
  // tap-to-fill alternatives so the reschedule never terminates in a wall.
  const shouldShowNextSlots = Boolean(
    selectedDate && availability && (!hasSlots || sameAsExisting)
  );

  const nextSlotSuggestions = useMemo(() => {
    if (!shouldShowNextSlots) return [];
    return getNextAvailableSlots({
      availability,
      computeSlotsForDate,
      isDateDisabled,
      fromDate: selectedDate,
      fromTime: sameAsExisting ? booking?.time : null,
      count: 3,
    });
  }, [shouldShowNextSlots, availability, computeSlotsForDate, isDateDisabled, selectedDate, sameAsExisting, booking?.time]);

  const handlePickSuggestion = (suggestion) => {
    setSelectedDate(suggestion.date);
    setSelectedTime(suggestion.time);
  };

  // Refresh calendar right when a user taps into a dead-end so the
  // suggestions reflect any bookings that landed in the last few seconds.
  useEffect(() => {
    if (!shouldShowNextSlots || !open) return;
    setSuggestionsLoading(true);
    loadCalendar().finally(() => setSuggestionsLoading(false));
  }, [shouldShowNextSlots, open, loadCalendar]);

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please pick a date and time');
      return;
    }
    if (sameAsExisting) {
      toast.error('Pick a different date or time from your current booking.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/bookings/${booking.booking_id}/reschedule`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        reason: reason.trim() || undefined,
      });
      // Server tells us who did the rescheduling so copy reads correctly for
      // both sides. Provider-initiated moves land immediately, client-initiated
      // ones wait for approval.
      if (res.data?.rescheduled_by === 'provider') {
        toast.success('Booking rescheduled, the client has been notified');
      } else {
        toast.success('Reschedule request sent to the provider');
      }
      onRescheduled?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-[#D4AF37]" />
            Request Reschedule
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            Pick a new date and time from the provider's live availability. Only open slots are shown. The provider still needs to approve the change.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4" data-testid="reschedule-dialog">
          <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">
            Current appointment: <strong className="text-[#1A1A1A]">{booking?.date}</strong> at <strong className="text-[#1A1A1A]">{booking?.time}</strong>, <strong className="text-[#1A1A1A]">{booking?.service_name}</strong>
          </div>

          {loading && (
            <div className="py-10 text-center text-sm text-stone-400" data-testid="reschedule-loading">
              <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin text-[#D4AF37]" />
              Loading the provider's calendar...
            </div>
          )}

          {!loading && fetchError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex gap-2 text-sm text-red-700" data-testid="reschedule-error">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              {fetchError}
            </div>
          )}

          {!loading && !fetchError && availability && (
            <>
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-1.5">New date</p>
                <div className="rounded-lg border border-stone-200 overflow-hidden bg-white">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setSelectedTime(''); }}
                    disabled={isDateDisabled}
                    initialFocus
                    data-testid="reschedule-calendar"
                  />
                </div>
              </div>

              {selectedDate && (
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A] mb-1.5">New time</p>
                  {!hasSlots ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      No available slots for this date, try another day.
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2" data-testid="reschedule-time-grid">
                      {slots.map((s) => {
                        const disabled = s.status !== 'available';
                        const active = s.time === selectedTime;
                        return (
                          <button
                            key={s.time}
                            type="button"
                            disabled={disabled}
                            onClick={() => setSelectedTime(s.time)}
                            className={[
                              'h-9 rounded-md border text-sm transition-colors',
                              disabled
                                ? 'border-stone-200 bg-stone-100 text-stone-400 line-through cursor-not-allowed'
                                : active
                                  ? 'border-[#D4AF37] bg-[#D4AF37] text-white font-medium'
                                  : 'border-stone-200 text-[#1A1A1A] hover:bg-stone-50',
                            ].join(' ')}
                            data-testid={`reschedule-slot-${s.time}`}
                          >
                            {s.time}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {sameAsExisting && (
                    <p className="text-xs text-amber-700 mt-2" data-testid="reschedule-same-as-existing-hint">
                      That matches your current booking. Pick one of the nearest alternatives below.
                    </p>
                  )}
                  {shouldShowNextSlots && (
                    <NextAvailableSlots
                      suggestions={nextSlotSuggestions}
                      onPick={handlePickSuggestion}
                      loading={suggestionsLoading}
                      testIdPrefix="reschedule-next-slot"
                    />
                  )}
                </div>
              )}

              {/* Optional reason, included in the SMS + email the counterparty
                  receives. Surfaces real context for the other side, a "why"
                  turns a schedule change from friction into care. */}
              {selectedDate && selectedTime && (
                <div data-testid="reschedule-reason-block">
                  <label htmlFor="reschedule-reason" className="text-sm font-medium text-[#1A1A1A] mb-1.5 flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5 text-stone-400" />
                    Add a note <span className="text-xs text-stone-400 font-normal">(optional, shared with the other side)</span>
                  </label>
                  <Textarea
                    id="reschedule-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, 280))}
                    placeholder="e.g. Sorry, I have another client overrun, I've moved you to Thursday morning."
                    rows={3}
                    className="resize-none"
                    data-testid="reschedule-reason-input"
                  />
                  <p className="text-[11px] text-stone-400 mt-1">{reason.length}/280</p>
                </div>
              )}
            </>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="reschedule-cancel-btn">
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !selectedDate || !selectedTime || loading || sameAsExisting}
              data-testid="confirm-reschedule-btn"
            >
              {submitting ? 'Requesting...' : 'Request reschedule'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
